import { useEffect, useState } from "react";

/**
 * useDesignTheme — shared preview color theme.
 * Persisted to localStorage so the banner picker sticks across pages.
 *
 * Each theme exposes:
 *  - id, label
 *  - gradient: tailwind classes for hero background
 *  - accent: primary accent hex (used for topbar buttons, active states)
 */

export const DP_THEMES = {
  fire:     { id: "fire",     label: "Fire",     swatch: ["#FF6A2B", "#B12FBB", "#5B3AE0"], gradient: "bg-gradient-to-r from-[#FF6A2B] via-[#B12FBB] to-[#5B3AE0]", accent: "#00C4CC" },
  cool:     { id: "cool",     label: "Cool",     swatch: ["#3B82F6", "#5B3AE0", "#8B5CF6"], gradient: "bg-gradient-to-r from-[#3B82F6] via-[#5B3AE0] to-[#8B5CF6]", accent: "#00C4CC" },
  teal:     { id: "teal",     label: "Teal",     swatch: ["#0EA5A4", "#00C4CC", "#3B82F6"], gradient: "bg-gradient-to-r from-[#0EA5A4] via-[#00C4CC] to-[#3B82F6]", accent: "#F5B93F" },
  sunset:   { id: "sunset",   label: "Sunset",   swatch: ["#F5B93F", "#FF6A2B", "#E64C5A"], gradient: "bg-gradient-to-r from-[#F5B93F] via-[#FF6A2B] to-[#E64C5A]", accent: "#00C4CC" },
  forest:   { id: "forest",   label: "Forest",   swatch: ["#22C6A6", "#16A34A", "#0F766E"], gradient: "bg-gradient-to-r from-[#22C6A6] via-[#16A34A] to-[#0F766E]", accent: "#F5B93F" },
  slate:    { id: "slate",    label: "Slate",    swatch: ["#1E293B", "#334155", "#475569"], gradient: "bg-gradient-to-r from-[#1E293B] via-[#334155] to-[#475569]", accent: "#00C4CC" },
  midnight: { id: "midnight", label: "Midnight", swatch: ["#0F1F3A", "#1a2d4f", "#0F1F3A"], gradient: "bg-gradient-to-r from-[#0F1F3A] via-[#1a2d4f] to-[#0F1F3A]", accent: "#00C4CC" },
};

const KEY = "dp-theme";
const listeners = new Set();

function readTheme() {
  try {
    const id = localStorage.getItem(KEY) || "fire";
    return DP_THEMES[id] || DP_THEMES.fire;
  } catch {
    return DP_THEMES.fire;
  }
}

export function useDesignTheme() {
  const [theme, setThemeState] = useState(readTheme);

  useEffect(() => {
    const cb = () => setThemeState(readTheme());
    listeners.add(cb);
    return () => { listeners.delete(cb); };
  }, []);

  const setTheme = (id) => {
    if (!DP_THEMES[id]) return;
    try { localStorage.setItem(KEY, id); } catch {}
    listeners.forEach(fn => fn());
  };

  return { theme, setTheme, themes: DP_THEMES };
}
